import { Injectable, inject } from '@angular/core';
import { Localization } from '@freelancer/localization';
import { ModalService } from '@freelancer/ui';
import { ModalSize } from '@freelancer/ui/modal';
import {
  AdyenAccountKey,
  AdyenPlatform,
  ADYEN_CONFIG,
  DEFAULT_MERCHANT_ACCOUNT,
  MERCHANT_ACCOUNT_MAP,
} from './adyen-3ds.config';
import {
  ThreeDSStatusCode,
  type ThreeDSHandler,
  type ThreeDSResult,
} from '../three-ds.types';

/**
 * Adyen-specific configuration for 3DS modal
 */
export interface Adyen3dsModalConfig {
  clientKey: string;
  platform: AdyenPlatform;
  locale: string;
  countryCode: string;
}

/**
 * Adyen 3DS handler
 * Handles 3DS authentication for Adyen payment gateway using modal-based approach.
 */
@Injectable({
  providedIn: 'root',
})
export class AdyenThreeDSHandler implements ThreeDSHandler {
  private readonly modalService = inject(ModalService);
  private readonly localization = inject(Localization);
  private readonly adyenConfig = inject(ADYEN_CONFIG);

  /**
   * Get the appropriate account configuration key for a merchant account
   * Uses the merchant account mapping to determine the correct config
   */
  private getAccountKey(merchantAccount: string): AdyenAccountKey {
    const accountConfigKey = MERCHANT_ACCOUNT_MAP[merchantAccount];
    if (!accountConfigKey) {
      throw new Error('Adyen 3DS handler: Merchant account is not supported');
    }
    return accountConfigKey;
  }

  /**
   * Build and validate the config for the Adyen modal
   * Uses environment config for clientKey and platform
   * Selects the appropriate account based on merchantAccount mapping
   */
  private buildModalConfig(merchantAccount: string): Adyen3dsModalConfig {
    // Adyen expects locale in format "en_US" (underscore), not "en-US" (hyphen)
    const locale = this.localization.locale.replace('-', '_');
    // Country code is irrelevant if we are only using the sdk for 3DS handling
    const countryCode = this.localization.countryCode ?? 'US';

    // Select the appropriate account configuration based on merchant account
    const accountKey = this.getAccountKey(merchantAccount);
    const accountConfig = this.adyenConfig[accountKey];
    const { clientKey, platform } = accountConfig;

    // Validate required config fields
    if (!clientKey) {
      throw new Error('Adyen 3DS handler: Client key is not configured');
    }
    if (!platform) {
      throw new Error('Adyen 3DS handler: Platform is not configured');
    }

    return {
      clientKey,
      platform,
      locale,
      countryCode,
    };
  }

  /**
   * Handle Adyen 3DS authentication by opening a modal
   * and mounting the Adyen 3DS component.
   *
   * @param payload The Adyen action payload (psp_payload from backend)
   * @param submitCallback Callback to submit 3DS results to backend
   * @returns Promise that resolves with the 3DS result
   */
  async handle(
    payload: string,
    submitCallback: (pspResults: any) => Promise<any>,
  ): Promise<ThreeDSResult> {
    const pspPayload = JSON.parse(payload);
    const { action, merchantAccount } = pspPayload;
    const config = this.buildModalConfig(
      merchantAccount ?? DEFAULT_MERCHANT_ACCOUNT,
    );

    // Open the modal with the 3DS container
    const modalRef = this.modalService.open('Adyen3dsModal', {
      inputs: {
        config,
        action,
        submitDetails: submitCallback,
      },
      size: ModalSize.FIT_CONTENT,
      closeable: true,
    });

    // Wait for modal to close and handle the result
    const result = (await modalRef.afterClosed()) as ThreeDSResult | undefined;

    if (!result) {
      // Modal was closed without a result (e.g., user clicked outside or pressed ESC before any submission)
      throw new Error('3DS authentication cancelled by user');
    }

    // If there's an error in the result, throw it
    if (result.error) {
      throw result.error;
    }

    // Handle cancelled status
    if (result.status === ThreeDSStatusCode.CANCELLED) {
      throw new Error('3DS authentication cancelled by user');
    }

    return result;
  }
}
